import { BillInvoice, ContractedPlace, EnergyDataPoint, PortalCredential, ScrapeLogEntry, UserAccount } from '../types/energy';
import { 
  generateHourlyUsage, 
  generateDailyUsage, 
  generateMonthlyUsage, 
  INITIAL_ACCOUNT, 
  INITIAL_INVOICES, 
  DEMO_INVOICES,
  DEFAULT_TARIFF,
  REAL_CONTRACTED_PLACES,
  getPlaceData
} from './mockData';
import { getSqliteSyncedBundle, saveLocalSyncedBundle } from './localSyncStorage';
import {
  getSqliteCredentials,
  saveSqliteCredentials,
  getSqliteLogs,
  saveSqliteLogs
} from './sqliteDatabase';
import type { Language } from '../i18n/translations';
import {
  activeRosterPlaceId,
  countConfirmedStatements,
  isPlaceRosterHeader,
  isStatementLedgerHeader,
  mapPlaceRosterRows,
  mapStatementLedgerRows,
  RosterRow,
  StatementLedgerEntry
} from './portalPlaces';
import {
  buildIsoDate,
  classifyPeriodFromXAxis,
  contextFromUrl,
  jstTodayIso,
  normalizeDailyTimestamp,
  normalizeMonthlyTimestamp,
  pad2,
  readPayloadUrl,
  resolveMonthTickYears,
  resolveTickYear
} from './telemetryPeriod';

const STORAGE_KEY_CREDS = 'symenergy_portal_credentials_v3';
const STORAGE_KEY_PW = 'symenergy_portal_password_v3';

// In-memory fast cache (empty by default for publishing)
let inMemoryCredentials: PortalCredential = {
  email: '',
  portalUrl: 'https://www.symenergy.net/symenergy/login',
  dashboardUrl: 'https://www.symenergy.net/symenergy/dashboard',
  rememberSession: false,
  lastSyncAt: '',
  syncIntervalMinutes: 60,
  sessionValid: false
};
let inMemoryPassword = '';
let inMemoryLogs: ScrapeLogEntry[] = [];

// Synchronously hydrate from localStorage if available (instant restore on reload)
try {
  if (typeof localStorage !== 'undefined') {
    const cachedCreds = localStorage.getItem(STORAGE_KEY_CREDS);
    const cachedPw = localStorage.getItem(STORAGE_KEY_PW);
    if (cachedCreds) {
      const parsed = JSON.parse(cachedCreds);
      if (parsed && typeof parsed === 'object') {
        inMemoryCredentials = { ...inMemoryCredentials, ...parsed };
      }
    }
    if (cachedPw) {
      inMemoryPassword = cachedPw;
    }
  }
} catch {
  // Ignore fallback error
}

// Background load from SQLite WASM
getSqliteCredentials().then(({ creds, password }) => {
  if (creds && (creds.email || creds.portalUrl)) {
    inMemoryCredentials = creds;
  }
  if (password) {
    inMemoryPassword = password;
  }
  try {
    if (typeof localStorage !== 'undefined') {
      if (creds && creds.email) {
        localStorage.setItem(STORAGE_KEY_CREDS, JSON.stringify(creds));
      }
      if (password) {
        localStorage.setItem(STORAGE_KEY_PW, password);
      }
    }
  } catch {}
}).catch(() => {});

getSqliteLogs().then((logs) => {
  if (logs && logs.length > 0) inMemoryLogs = logs;
}).catch(() => {});

export function getStoredCredentials(): PortalCredential {
  return inMemoryCredentials;
}

export function getStoredPassword(): string {
  return inMemoryPassword;
}

/**
 * Unified, atomic save for credentials and password.
 * Persists immediately to localStorage and SQLite WASM IndexedDB.
 */
export async function saveCredentials(creds: PortalCredential, pw: string): Promise<void> {
  inMemoryCredentials = creds;
  inMemoryPassword = pw;

  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_CREDS, JSON.stringify(creds));
      localStorage.setItem(STORAGE_KEY_PW, pw);
    }
  } catch (err) {
    console.warn('[Credentials] localStorage write notice:', err);
  }

  await saveSqliteCredentials(creds, pw);
}

export function saveStoredCredentials(creds: PortalCredential): void {
  inMemoryCredentials = creds;
  saveCredentials(creds, inMemoryPassword).catch((err) => {
    console.error('[SQLite WASM] Failed saving credentials to SQLite', err);
  });
}

export function saveStoredPassword(pw: string): void {
  inMemoryPassword = pw;
  saveCredentials(inMemoryCredentials, pw).catch((err) => {
    console.error('[SQLite WASM] Failed saving password to SQLite', err);
  });
}

export function getStoredLogs(): ScrapeLogEntry[] {
  return inMemoryLogs;
}

export function saveStoredLogs(logs: ScrapeLogEntry[]): void {
  inMemoryLogs = logs;
  saveSqliteLogs(logs).catch((err) => {
    console.error('[SQLite WASM] Failed saving logs to SQLite', err);
  });
}

export interface ScrapeExecutionResult {
  success: boolean;
  account: UserAccount;
  places: ContractedPlace[];
  invoices: BillInvoice[];
  hourlyUsage: EnergyDataPoint[];
  dailyUsage: EnergyDataPoint[];
  monthlyUsage: EnergyDataPoint[];
  logs: ScrapeLogEntry[];
  debugCurls?: Record<string, string>;
}

/**
 * 100% Local Telemetry Ingestion & Parsing Engine.
 * Ingests SymEnergy HTML/JS/JSON bundles completely inside the client browser.
 * ZERO credentials or telemetry data are ever sent over the internet or to external servers.
 * Persists immediately into SQLite WASM (OPFS / IndexedDB).
 */
export async function importLocalTelemetry(
  rawInput: string | string[],
  onStepUpdate?: (step: string, progressPct: number) => void,
  lang: Language = 'ja'
): Promise<ScrapeExecutionResult> {
  const L = (ja: string, en: string) => (lang === 'en' ? en : ja);

  if (onStepUpdate) {
    onStepUpdate(L('100% ローカル解析エンジンを起動中 (端末内処理)...', 'Starting 100% local parsing engine (on-device)...'), 20);
  }

  // 1. Expand input payloads (support Bookmarklet JSON bundle or array of HTML strings)
  let payloads: string[] = [];
  if (Array.isArray(rawInput)) {
    payloads = rawInput;
  } else if (typeof rawInput === 'string') {
    const trimmed = rawInput.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsedJson = JSON.parse(trimmed);
        if (Array.isArray(parsedJson.htmlPayloads)) {
          payloads = parsedJson.htmlPayloads;
        } else if (parsedJson.html) {
          payloads = [parsedJson.html];
        } else {
          payloads = [trimmed];
        }
      } catch {
        payloads = [trimmed];
      }
    } else {
      payloads = [trimmed];
    }
  }

  if (payloads.length === 0) {
    throw new Error(L('取り込み可能なシン・エナジーのデータが見つかりませんでした。', 'No importable SymEnergy data was found.'));
  }

  if (onStepUpdate) {
    onStepUpdate(L('HTML・C3.js スマートメーター変数を解析中...', 'Parsing HTML & C3.js smart meter variables...'), 50);
  }

  const currentBundle = await getSqliteSyncedBundle();
  let mergedHourly = [...(currentBundle.hourlyUsage || [])];
  let mergedDaily = [...(currentBundle.dailyUsage || [])];
  let mergedMonthly = [...(currentBundle.monthlyUsage || [])];
  let mergedInvoices = [...(currentBundle.invoices || [])];
  let mergedPlaces = [...(currentBundle.places || [])];

  // Stored rows can predate the chart-classification fix: month-axis ticks were saved as days
  // (`2026-10月`) and day rows were saved unpadded (`2026-9-1`). Both sort after real days
  // lexicographically, so they corrupt the displayed range and day count until they are purged.
  mergedHourly = mergedHourly.flatMap((row) => {
    const [date, time] = (row.timestamp || '').split(' ');
    const iso = normalizeDailyTimestamp(date);
    return iso ? [{ ...row, timestamp: `${iso} ${time || '00:00'}` }] : [];
  });
  mergedDaily = mergedDaily.flatMap((row) => {
    const iso = normalizeDailyTimestamp(row.timestamp);
    return iso ? [{
      ...row,
      timestamp: iso,
      kwh: Number((row.kwh ?? 0).toFixed(2)),
      dayKwh: Number((row.dayKwh || 0).toFixed(2)),
      lifeKwh: Number((row.lifeKwh || 0).toFixed(2)),
      nightKwh: Number((row.nightKwh || 0).toFixed(2))
    }] : [];
  });
  mergedMonthly = mergedMonthly.flatMap((row) => {
    const iso = normalizeMonthlyTimestamp(row.timestamp);
    return iso ? [{ ...row, timestamp: iso }] : [];
  });

  let extractedAccountInfo: Partial<UserAccount> = {};
  let extractedPlaceInfo: Partial<ContractedPlace> = {};
  let detectedPlaceId = currentBundle.selectedPlaceId || '';

  // Roster rows and statement counts are collected per place id across all payloads: the portal
  // repeats the roster on /demands, /meter_readings and /amounts, and the ledger on /amounts/{id}.
  const rosterById: Record<string, Partial<ContractedPlace>> = {};
  const statementCounts: Record<string, number> = {};
  const statementsById: Record<string, StatementLedgerEntry[]> = {};

  let totalParsedPoints = 0;
  let totalParsedInvoices = 0;

  for (const html of payloads) {
    if (!html || typeof html !== 'string') continue;
    const parsed = parseRawPortalHtml(html);
    // The monthly chart covers the portal's whole 12-month window, so an empty result is
    // still authoritative: keeping stored months after a chart that measured none would
    // resurrect rows the portal never reported. Read before the success gate for that reason.
    if (parsed.extractedMonthlyUsage) mergedMonthly = parsed.extractedMonthlyUsage;
    if (!parsed.success) continue;

    if (parsed.detectedPlaceId && !detectedPlaceId) {
      detectedPlaceId = parsed.detectedPlaceId;
    }
    if (parsed.extractedPlaces && parsed.extractedPlaces.length > 0) {
      for (const roster of parsed.extractedPlaces) {
        if (!roster.id) continue;
        rosterById[roster.id] = { ...rosterById[roster.id], ...roster };
      }
    }
    if (parsed.confirmedStatementCount !== undefined && parsed.statementPlaceId) {
      const previous = statementCounts[parsed.statementPlaceId];
      statementCounts[parsed.statementPlaceId] = Math.max(previous ?? 0, parsed.confirmedStatementCount);
    }
    if (parsed.extractedAccount) {
      extractedAccountInfo = { ...extractedAccountInfo, ...parsed.extractedAccount };
    }
    if (parsed.extractedPlace) {
      extractedPlaceInfo = { ...extractedPlaceInfo, ...parsed.extractedPlace };
    }
    if (parsed.extractedInvoices && parsed.extractedInvoices.length > 0) {
      for (const inv of parsed.extractedInvoices as BillInvoice[]) {
        const idx = mergedInvoices.findIndex((x) => x.invoiceNumber === inv.invoiceNumber);
        if (idx >= 0) mergedInvoices[idx] = inv;
        else mergedInvoices.push(inv);
        totalParsedInvoices++;
      }
    }
    if (parsed.extractedHourlyUsage && parsed.extractedHourlyUsage.length > 0) {
      const dateIso = parsed.extractedHourlyUsage[0]?.timestamp?.split(' ')[0];
      mergedHourly = [
        ...mergedHourly.filter((h) => !dateIso || !h.timestamp?.startsWith(dateIso)),
        ...parsed.extractedHourlyUsage
      ].sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || ''));
      totalParsedPoints += parsed.extractedHourlyUsage.length;
    }
    if (parsed.extractedDailyUsage && parsed.extractedDailyUsage.length > 0) {
      for (const extD of parsed.extractedDailyUsage) {
        const iso = normalizeDailyTimestamp(extD.timestamp);
        if (!iso) continue;
        const row = { ...extD, timestamp: iso };
        // The padded ISO timestamp is the identity of a daily row. The previous label clause
        // (`split('(')[0]`) could never match `10月` against `10/10`, so stale rows from the
        // misclassified monthly chart were re-appended on every sync instead of replaced.
        const idx = mergedDaily.findIndex((d) => d.timestamp === iso);
        if (idx >= 0) mergedDaily[idx] = row;
        else mergedDaily.push(row);
      }
      mergedDaily.sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || ''));
    }
    if (parsed.extractedStatements && parsed.extractedStatements.length > 0 && parsed.statementPlaceId) {
      statementsById[parsed.statementPlaceId] = parsed.extractedStatements;
    }
  }

  let realAddress = extractedPlaceInfo.address || extractedAccountInfo.serviceAddress || currentBundle.account?.serviceAddress || '';
  let realSupplyPointId = extractedPlaceInfo.supplyPointId || currentBundle.account?.tariff?.mprnNumber || '';

  try {
    if (typeof localStorage !== 'undefined') {
      if (realAddress) localStorage.setItem('symenergy_saved_service_address', realAddress);
      if (realSupplyPointId) localStorage.setItem('symenergy_saved_supply_point_id', realSupplyPointId);
    }
  } catch {}

  // Without a chart payload there is no url-derived place, so the portal's own roster decides
  // which location is current: the one it lists as 契約中.
  const rosterPlaces = Object.entries(rosterById).map(([id, place]) => ({ id, status: place.status }));
  const activePlaceId =
    detectedPlaceId ||
    activeRosterPlaceId(rosterPlaces) ||
    rosterPlaces[0]?.id ||
    currentBundle.selectedPlaceId ||
    currentBundle.account?.currentPlaceId ||
    'default';
  const creds = getStoredCredentials();
  const holderName = extractedAccountInfo.accountHolder || currentBundle.account?.accountHolder || creds.email || 'お客さま';

  // Place identity, contract status and label come from the portal's own roster rows. Nothing is
  // invented per place id: a place the roster never listed keeps whatever the detail pages proved.
  // Rows are read back filtered by place_id, so whatever key is used here must have a matching
  // contracted_places row, otherwise the data is unreachable after a reload.
  const placeIdsToMerge = new Set(Object.keys(rosterById));
  placeIdsToMerge.add(activePlaceId);

  for (const placeId of placeIdsToMerge) {
    const rosterPlace = rosterById[placeId] || {};
    const detailPlace = placeId === activePlaceId ? extractedPlaceInfo : {};
    const storedPlace = mergedPlaces.find((p) => p.id === placeId);
    const placeObj: ContractedPlace = {
      id: placeId,
      demandNumber: rosterPlace.demandNumber || detailPlace.demandNumber || storedPlace?.demandNumber || placeId,
      status: rosterPlace.status ?? detailPlace.status ?? storedPlace?.status,
      displayName: rosterPlace.displayName || detailPlace.displayName || storedPlace?.displayName || '',
      contractName: rosterPlace.contractName || detailPlace.contractName || storedPlace?.contractName || holderName,
      planName:
        rosterPlace.planName ||
        detailPlace.planName ||
        storedPlace?.planName ||
        currentBundle.account?.tariff?.name ||
        '',
      address: rosterPlace.address || detailPlace.address || storedPlace?.address || realAddress,
      powerArea: rosterPlace.powerArea || detailPlace.powerArea || storedPlace?.powerArea || '',
      supplyPointId: rosterPlace.supplyPointId || detailPlace.supplyPointId || realSupplyPointId,
      paymentGroup: rosterPlace.paymentGroup || detailPlace.paymentGroup || storedPlace?.paymentGroup || '',
      contractCapacity:
        rosterPlace.contractCapacity ||
        detailPlace.contractCapacity ||
        extractedAccountInfo.contractCapacity ||
        currentBundle.account?.contractCapacity ||
        '',
      startDate: rosterPlace.startDate || detailPlace.startDate || storedPlace?.startDate || '',
      contractDate: rosterPlace.contractDate || detailPlace.contractDate || storedPlace?.contractDate || '',
      invoicesCount: mergedInvoices.length,
      confirmedStatementCount: statementCounts[placeId]
    };

    const existingIdx = mergedPlaces.findIndex((p) => p.id === placeId);
    if (existingIdx >= 0) mergedPlaces[existingIdx] = placeObj;
    else mergedPlaces.push(placeObj);
  }

  const syncedAccount: UserAccount = {
    ...currentBundle.account,
    accountNumber: extractedAccountInfo.accountNumber || creds.email || currentBundle.account?.accountNumber,
    accountHolder: holderName,
    serviceAddress: realAddress,
    contractCapacity: extractedPlaceInfo.contractCapacity || extractedAccountInfo.contractCapacity || currentBundle.account?.contractCapacity || '',
    paymentDetails: currentBundle.account?.paymentDetails || '',
    currentPlaceId: activePlaceId,
    contractedPlaces: mergedPlaces,
    tariff: {
      ...DEFAULT_TARIFF,
      mprnNumber: realSupplyPointId,
      name: extractedPlaceInfo.planName || currentBundle.account?.tariff?.name || DEFAULT_TARIFF.name
    }
  };

  if (onStepUpdate) {
    onStepUpdate(L('SQLite WASM データベースへ安全に保存中...', 'Saving securely to SQLite WASM database...'), 90);
  }

  const nowIso = new Date().toISOString();
  creds.lastSyncAt = nowIso;
  creds.sessionValid = true;
  saveStoredCredentials(creds);

  const existingLogs = getStoredLogs();
  const localSuccessLog: ScrapeLogEntry = {
    id: `local-import-${Date.now()}`,
    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    step: L('ローカル取り込み & SQLite WASM保存', 'Local import & SQLite WASM save'),
    status: 'success',
    details: L(
      `100%端末内解析完了: 30分値 ${mergedHourly.length}コマ、日別 ${mergedDaily.length}日分、検針票 ${mergedInvoices.length}件を統合しました (外部通信なし・認証情報非送信)。`,
      `On-device parsing complete: merged ${mergedHourly.length} 30-min intervals, ${mergedDaily.length} days, and ${mergedInvoices.length} statements (no external transmission).`
    )
  };
  const mergedLogs = [localSuccessLog, ...existingLogs];
  saveStoredLogs(mergedLogs);

  // The statement ledger is the only source of 検針日 / 使用日数 / 請求金額, so chart months keep
  // their measured kWh and lose every derived billing figure. A month with no confirmed
  // statement is therefore never stored or rendered as if it had been billed, and legacy rows
  // synthesized before this change are cleaned here instead of being kept as-is.
  const monthlyByKey = new Map<string, EnergyDataPoint>();
  for (const row of mergedMonthly) {
    const iso = normalizeMonthlyTimestamp(row.timestamp);
    if (!iso || row.statementConfirmed) continue;
    monthlyByKey.set(iso.slice(0, 7), {
      ...row,
      timestamp: iso,
      cost: 0,
      readingDate: undefined,
      daysCount: undefined,
      dailyAvgKwh: undefined,
      amountExcludingLevy: undefined
    });
  }
  for (const entry of statementsById[activePlaceId] || []) {
    const key = `${entry.year}-${pad2(entry.month)}`;
    monthlyByKey.set(key, {
      ...(monthlyByKey.get(key) || {}),
      timestamp: normalizeMonthlyTimestamp(key) || `${key}-01`,
      label: `${entry.year}年${entry.month}月`,
      kwh: entry.kwh,
      cost: entry.amountInclTax,
      amountExcludingLevy: entry.amountExclLevy,
      readingDate: entry.readingDate,
      daysCount: entry.daysCount,
      dailyAvgKwh: entry.dailyAvgKwh,
      statementConfirmed: true,
      isPeak: false
    });
  }
  mergedMonthly = [...monthlyByKey.values()].sort((a, b) =>
    (a.timestamp || '').localeCompare(b.timestamp || '')
  );

  await saveLocalSyncedBundle({
    account: syncedAccount,
    places: mergedPlaces,
    selectedPlaceId: activePlaceId,
    invoices: mergedInvoices,
    hourlyUsage: mergedHourly,
    dailyUsage: mergedDaily,
    monthlyUsage: mergedMonthly,
    lastSyncAt: nowIso,
    autoSyncEnabled: true
  });

  if (onStepUpdate) {
    onStepUpdate(L('取り込み完了！', 'Import complete!'), 100);
  }

  return {
    success: true,
    account: syncedAccount,
    places: mergedPlaces,
    invoices: mergedInvoices,
    hourlyUsage: mergedHourly,
    dailyUsage: mergedDaily,
    monthlyUsage: mergedMonthly,
    logs: mergedLogs
  };
}

/**
 * 1-Click Cloudflare Worker Proxy Scraper Engine
 * Calls the symenergy-proxy Worker (Cloudflare Workers) which handles
 * the actual fetch to symenergy.net server-side (no CORS, no IP blocking).
 * The Worker returns HTML payloads, which are parsed client-side by importLocalTelemetry().
 */
export async function executePortalScrape(
  onStepUpdate?: (step: string, progressPct: number) => void,
  lang: Language = 'ja'
): Promise<ScrapeExecutionResult> {
  const L = (ja: string, en: string) => (lang === 'en' ? en : ja);

  const creds = getStoredCredentials();
  const password = getStoredPassword();
  const rawEmail = creds.email?.trim() || '';

  if (!rawEmail || !password) {
    throw new Error(L(
      'ログインID（メールアドレスまたはお客さま番号）とパスワードが未設定です。マイページ設定画面で入力してください。',
      'Login ID (email or customer number) and password are not set. Please enter them on the MyPage settings screen.'
    ));
  }

  const existingLogs = getStoredLogs();

  // Worker URL: use env variable in production, or localhost:8787 in dev
  const WORKER_URL = import.meta.env.VITE_WORKER_URL || 'http://localhost:8787';

  if (onStepUpdate) {
    onStepUpdate(L('1. Cloudflare Worker 経由でスクレイピング実行中...', '1. Scraping via Cloudflare Worker...'), 20);
  }

  let workerResponse: {
    success: boolean;
    htmlPayloads?: string[];
    logs?: Array<{
      id: string;
      timestamp: string;
      step: string;
      status: 'success' | 'error' | 'warn';
      details: string;
      requestUrl?: string;
      httpStatus?: number;
    }>;
    error?: string;
  };

  // Only genuine transport/parse failures belong here. Portal-level failures
  // (bad credentials, session expired) must propagate with the Worker's own message.
  try {
    const res = await fetch(`${WORKER_URL}/api/scrape`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email: rawEmail, password, lang }),
    });

    workerResponse = await res.json();
  } catch (err: any) {
    const errDetail = err?.message || String(err);
    const failLog: ScrapeLogEntry = {
      id: `worker-err-${Date.now()}`,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      step: L('Cloudflare Worker 通信', 'Cloudflare Worker request'),
      status: 'error',
      details: L(`Worker通信エラー: ${errDetail}`, `Worker request error: ${errDetail}`),
    };

    saveStoredLogs([failLog, ...existingLogs]);
    throw new Error(L(`Worker通信エラー: ${errDetail}`, `Worker request error: ${errDetail}`));
  }

  const workerLogs: ScrapeLogEntry[] = (workerResponse.logs || []).map((log) => ({
    id: log.id,
    timestamp: log.timestamp,
    step: log.step,
    status: log.status,
    details: log.details,
    requestUrl: log.requestUrl,
    httpStatus: log.httpStatus,
  }));

  if (!workerResponse.success) {
    saveStoredLogs([...workerLogs, ...existingLogs]);
    throw new Error(workerResponse.error || 'Worker scrape failed');
  }

  if (onStepUpdate) {
    onStepUpdate(L('2. WorkerからHTMLペイロードを受信済み。端末内で解析中...', '2. HTML payloads received from Worker. Parsing on-device...'), 60);
  }

  // Parse HTML payloads client-side (100% local, no network)
  const htmlPayloads = workerResponse.htmlPayloads || [];

  if (htmlPayloads.length === 0) {
    saveStoredLogs([...workerLogs, ...existingLogs]);
    throw new Error(L('WorkerからHTMLペイロードが返されませんでした。', 'The Worker returned no HTML payloads.'));
  }

  if (onStepUpdate) {
    onStepUpdate(L('3. スマートメーターテレメトリを端末内SQLite WASMへ保存中...', '3. Saving smart meter telemetry to on-device SQLite WASM...'), 80);
  }

  const result = await importLocalTelemetry(htmlPayloads, onStepUpdate, lang);

  // result.logs already carries the prior history, so appending existingLogs
  // again would duplicate every older entry in the ledger.
  const mergedLogs = [...workerLogs, ...result.logs];
  saveStoredLogs(mergedLogs);

  return {
    ...result,
    logs: mergedLogs,
  };
}

/**
 * Extracts JavaScript objects or arrays defined in <script> tags or raw JS,
 * such as:
 * var days = ["10\u670812\u65e5(\u6708)", ...];
 * var amountColumns = [["x", "10\/10", ...], ["\u30ca\u30a4\u30c8\u30bf\u30a4\u30e0", ...], ...];
 */
export function extractJsVariable(src: string, varName: string): any {
  if (!src) return null;

  // Match: var|let|const|window.varName =
  const startRegex = new RegExp(`(?:var|let|const|window\\.)\\s*${varName}\\s*=\\s*`, 'm');
  const match = src.match(startRegex);
  if (!match) return null;

  const startIndex = match.index! + match[0].length;
  let pos = startIndex;
  while (pos < src.length && /\s/.test(src[pos])) pos++;
  if (pos >= src.length) return null;

  const openChar = src[pos];
  if (openChar !== '[' && openChar !== '{') return null;
  const closeChar = openChar === '[' ? ']' : '}';

  let depth = 0;
  let inStr = false;
  let strQuote = '';
  let endIndex = -1;

  for (let i = pos; i < src.length; i++) {
    const ch = src[i];
    const prev = i > pos ? src[i - 1] : '';

    if (inStr) {
      if (ch === strQuote && prev !== '\\') inStr = false;
    } else {
      if (ch === '"' || ch === "'" || ch === '`') {
        inStr = true;
        strQuote = ch;
      } else if (ch === openChar) {
        depth++;
      } else if (ch === closeChar) {
        depth--;
        if (depth === 0) {
          endIndex = i + 1;
          break;
        }
      }
    }
  }

  if (endIndex === -1) return null;
  const raw = src.substring(pos, endIndex);
  try {
    return JSON.parse(raw);
  } catch {
    try {
      return Function(`"use strict"; return (${raw})`)();
    } catch (e) {
      console.warn(`[JS Scraper] Could not evaluate variable ${varName}:`, e);
      return null;
    }
  }
}

export interface ExtractedJsTelemetry {
  dailyUsage?: EnergyDataPoint[];
  hourlyUsage?: EnergyDataPoint[];
  monthlyUsage?: EnergyDataPoint[];
  detectedPlaceId?: string;
  days?: string[];
  amountColumns?: any[][];
  dataPointsCount: number;
  sourceType: 'inline_script' | 'raw_js';
  summary: string;
}

/**
 * Extracts smart meter telemetry directly from SymEnergy portal JavaScript variables
 * (such as C3.js chart datasets in <script> tags) instead of brittle HTML table scraping.
 */
export function extractJsTelemetryFromSource(rawSource: string): ExtractedJsTelemetry | null {
  if (!rawSource) return null;

  // 1. Extract days array (formatted date labels e.g. ["10月12日(月)", "10月11日(日)", ...])
  const days: string[] | null = extractJsVariable(rawSource, 'days');

  // 2. Extract amountColumns matrix: [["x", "10/10", ...], ["ナイトタイム", ...], ["ライフタイム", ...], ["デイタイム", ...]]
  let amountColumns: any[][] | null = extractJsVariable(rawSource, 'amountColumns');
  if (!amountColumns || !Array.isArray(amountColumns)) {
    amountColumns = extractJsVariable(rawSource, 'data_columns') || extractJsVariable(rawSource, 'columns');
  }

  // 3. Fallback: check for C3 generate columns: [[...], [...]]
  if (!amountColumns) {
    const c3Match = rawSource.match(/columns\s*:\s*(\[\s*\[[\s\S]*?\]\s*\])/m);
    if (c3Match) {
      try {
        amountColumns = JSON.parse(c3Match[1]);
      } catch {
        try {
          amountColumns = Function(`"use strict"; return (${c3Match[1]})`)();
        } catch {
          // ignore
        }
      }
    }
  }

  if (!amountColumns || !Array.isArray(amountColumns) || amountColumns.length === 0) {
    return null;
  }

  // 4. Recover period, target date and place from the payload's own source URL when the proxy
  // marker is present (AI_RULES.md Rule 8.2); document scraping is only a bookmarklet fallback.
  const payloadContext = contextFromUrl(readPayloadUrl(rawSource));

  let detectedPlaceId: string | undefined = payloadContext.placeId || undefined;
  if (!detectedPlaceId) {
    const placeMatch = rawSource.match(/amounts\/([0-9]{5,})/i) || rawSource.match(/places\/([0-9]{5,})/i) || rawSource.match(/place[_-]?id\s*[:=]\s*["']?([0-9]{5,})/i);
    if (placeMatch && placeMatch[1]) {
      detectedPlaceId = placeMatch[1];
    }
  }

  // Target date for hourly pages (e.g. /amounts/318949/hourly/2026-10-14 -> 2026-10-14).
  // A bookmarklet capture carries no marker, so its first hourly link is the only signal left.
  let detectedDateIso = payloadContext.dateIso;
  if (!detectedDateIso) {
    const dateMatch = rawSource.match(/amounts\/[0-9]+\/hourly\/(\d{4}[-/]\d{1,2}[-/]\d{1,2})/i) ||
                      rawSource.match(/hourly\/(\d{4}[-/]\d{1,2}[-/]\d{1,2})/i) ||
                      rawSource.match(/["'](\d{4}-\d{2}-\d{2})["']/);
    detectedDateIso = dateMatch
      ? (normalizeDailyTimestamp(dateMatch[1].replace(/\//g, '-')) || jstTodayIso())
      : jstTodayIso();
  }

  // 5. Categorize C3 data columns
  const xCol = amountColumns.find((c) => Array.isArray(c) && c[0] === 'x') || amountColumns[0];
  const nightCol = amountColumns.find((c) => {
    if (!Array.isArray(c)) return false;
    const name = String(c[0]);
    return name.includes('ナイト') || name.toLowerCase().includes('night');
  });
  const lifeCol = amountColumns.find((c) => {
    if (!Array.isArray(c)) return false;
    const name = String(c[0]);
    return name.includes('ライフ') || name.toLowerCase().includes('life');
  });
  const dayCol = amountColumns.find((c) => {
    if (!Array.isArray(c)) return false;
    const name = String(c[0]);
    return name.includes('デイ') || name.toLowerCase().includes('day');
  });
  let genericUsageCol = amountColumns.find((c) => {
    if (!Array.isArray(c)) return false;
    const name = String(c[0]);
    return name !== 'x' && (name.includes('使用量') || name.includes('電力量') || name.toLowerCase().includes('kwh') || name.includes('\u4f7f\u7528\u91cf') || name.includes('使用'));
  });

  if (!genericUsageCol && amountColumns.length >= 2) {
    genericUsageCol = amountColumns.find((c) => Array.isArray(c) && String(c[0]) !== 'x') || amountColumns[1];
  }

  if (!xCol || xCol.length <= 1) {
    return null;
  }

  const weekdaysJa = ['日', '月', '火', '水', '木', '金', '土'];

  // The period comes from the payload's own URL when the proxy marker is present, and otherwise
  // from the x-axis shape. Document text is never consulted: every portal page links to all the
  // other period endpoints, so the old `/hourly/` substring test parsed the daily chart as hourly.
  const period = payloadContext.period ?? classifyPeriodFromXAxis(xCol.slice(1));

  if (!period) {
    console.warn('[JS Scraper] Unrecognised chart x-axis shape, skipping payload', xCol.slice(1, 5));
    return null;
  }

  if (period === 'hourly') {
    const hourlyData: EnergyDataPoint[] = [];
    const dateObj = new Date(detectedDateIso);
    const dayOfWeek = isNaN(dateObj.getTime()) ? 3 : dateObj.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    for (let i = 1; i < xCol.length; i++) {
      // `??` never fires for parseInt, which returns NaN rather than null/undefined.
      const parsedHour = parseInt(String(xCol[i]), 10);
      const hourNum = Number.isFinite(parsedHour) ? parsedHour : i - 1;
      const hourStr = `${String(hourNum).padStart(2, '0')}:00`;

      let tier: 'day' | 'life' | 'night' = 'night';
      let rate = 18.88;
      if (!isWeekend) {
        if (hourNum >= 23 || hourNum < 6) {
          tier = 'night';
          rate = 18.88;
        } else if ((hourNum >= 6 && hourNum < 9) || (hourNum >= 16 && hourNum < 23)) {
          tier = 'life';
          rate = 32.65;
        } else {
          tier = 'day';
          rate = 26.25;
        }
      } else {
        if (hourNum >= 22 || hourNum < 8) {
          tier = 'night';
          rate = 18.88;
        } else {
          tier = 'day';
          rate = 26.25;
        }
      }

      const night = Number(nightCol?.[i] || 0);
      const life = Number(lifeCol?.[i] || 0);
      const day = Number(dayCol?.[i] || 0);
      const genKwh = Number(genericUsageCol?.[i] || 0);

      const hasTierCols = (nightCol !== undefined || lifeCol !== undefined || dayCol !== undefined);
      const kwh = hasTierCols 
        ? Number((night + life + day).toFixed(3)) 
        : Number(genKwh.toFixed(3));

      const finalDayKwh = hasTierCols ? day : (tier === 'day' ? kwh : 0);
      const finalLifeKwh = hasTierCols ? life : (tier === 'life' ? kwh : 0);
      const finalNightKwh = hasTierCols ? night : (tier === 'night' ? kwh : 0);
      const cost = Math.round(finalNightKwh * 18.88 + finalLifeKwh * 32.65 + finalDayKwh * 26.25);

      hourlyData.push({
        timestamp: `${detectedDateIso} ${hourStr}`,
        label: hourStr,
        kwh,
        cost,
        isPeak: tier === 'life' || kwh >= 0.15,
        tier,
        tierRate: rate,
        dayKwh: finalDayKwh,
        lifeKwh: finalLifeKwh,
        nightKwh: finalNightKwh
      });
    }

    const totalDayKwh = Number(hourlyData.reduce((acc, h) => acc + h.kwh, 0).toFixed(2));
    const totalDayNight = Number(hourlyData.reduce((acc, h) => acc + (h.nightKwh || 0), 0).toFixed(2));
    const totalDayLife = Number(hourlyData.reduce((acc, h) => acc + (h.lifeKwh || 0), 0).toFixed(2));
    const totalDayDay = Number(hourlyData.reduce((acc, h) => acc + (h.dayKwh || 0), 0).toFixed(2));
    const totalDayCost = hourlyData.reduce((acc, h) => acc + h.cost, 0);

    const mAndD = detectedDateIso.split('-').slice(1).join('/'); // "10/14"
    const weekdayChar = ['日', '月', '火', '水', '木', '金', '土'][dayOfWeek] || '';
    const dayLabel = `${mAndD}(${weekdayChar})`;

    const derivedDailyPoint: EnergyDataPoint = {
      timestamp: detectedDateIso,
      label: dayLabel,
      kwh: totalDayKwh,
      cost: totalDayCost + (totalDayKwh > 0 ? 35 : 0),
      isPeak: totalDayKwh >= 2.5,
      dayKwh: totalDayDay,
      lifeKwh: totalDayLife,
      nightKwh: totalDayNight
    };

    return {
      hourlyUsage: hourlyData,
      dailyUsage: [derivedDailyPoint],
      detectedPlaceId,
      days: days || undefined,
      amountColumns,
      dataPointsCount: hourlyData.length,
      sourceType: rawSource.includes('<script') ? 'inline_script' : 'raw_js',
      summary: `Extracted ${hourlyData.length} hourly intervals for ${detectedDateIso} (Total: ${totalDayKwh} kWh, Night: ${totalDayNight} kWh)`
    };
  }

  if (period === 'monthly') {
    const monthlyData: EnergyDataPoint[] = [];

    // The 12-month chart ships 前年* and 今年* series pairs. The shared nightCol/lifeCol/dayCol
    // come from `find`, which binds them to the 前年 pair first, so re-select by year here.
    const namedSeries = (keywordJa: string, keywordEn: string, isPrevSeries: boolean) =>
      amountColumns!.find((c) => {
        if (!Array.isArray(c)) return false;
        const name = String(c[0]);
        const prev = name.includes('前年') || name.toLowerCase().includes('prev');
        return prev === isPrevSeries && (name.includes(keywordJa) || name.toLowerCase().includes(keywordEn));
      });

    const thisNight = namedSeries('ナイト', 'night', false) ?? nightCol;
    const thisLife = namedSeries('ライフ', 'life', false) ?? lifeCol;
    const thisDay = namedSeries('デイ', 'day', false) ?? dayCol;
    const lastNight = namedSeries('ナイト', 'night', true);
    const lastLife = namedSeries('ライフ', 'life', true);
    const lastDay = namedSeries('デイ', 'day', true);

    // Month ticks can omit the year (this chart's axis is `10月…9月`), so resolve the whole axis
    // at once: it ascends and ends on the most recent confirmed month.
    const tickMonths = xCol.slice(1).map((v) => {
      const rawX = String(v ?? '').trim();
      const explicit = /(\d{4})[年/-](\d{1,2})/.exec(rawX);
      if (explicit) return Number(explicit[2]);
      const bare = /^(\d{1,2})月$/.exec(rawX);
      return bare ? Number(bare[1]) : NaN;
    });
    const tickYears = resolveMonthTickYears(tickMonths.filter((m) => Number.isFinite(m)), detectedDateIso || jstTodayIso());
    let resolvedYearIdx = 0;

    for (let i = 1; i < xCol.length; i++) {
      const month = tickMonths[i - 1];
      if (!Number.isFinite(month) || month < 1 || month > 12) continue;

      const rawX = String(xCol[i]).trim();
      const explicitYear = /(\d{4})[年/-]\d{1,2}/.exec(rawX);
      const year = explicitYear
        ? Number(explicitYear[1])
        : tickYears[resolvedYearIdx++];

      const night = Number(thisNight?.[i] || 0);
      const life = Number(thisLife?.[i] || 0);
      const day = Number(thisDay?.[i] || 0);
      const genKwh = Number(genericUsageCol?.[i] || 0);
      const kwh = Number((night + life + day > 0 ? night + life + day : genKwh).toFixed(1));
      const prevNight = Number(lastNight?.[i] || 0);
      const prevLife = Number(lastLife?.[i] || 0);
      const prevDay = Number(lastDay?.[i] || 0);
      const prevKwh = Number((prevNight + prevLife + prevDay).toFixed(1));
      const hasPrevSeries = prevKwh > 0;

      // A chart axis tick is not a measurement: months the portal plots as zero - which on a
      // 10月…9月 axis includes the months still in the future - must not become rows.
      if (kwh <= 0 && prevKwh <= 0) continue;

      monthlyData.push({
        timestamp: normalizeMonthlyTimestamp(`${year}-${pad2(month)}`) || `${year}-${pad2(month)}`,
        label: `${year}年${month}月`,
        kwh,
        cost: 0,
        dayKwh: day,
        lifeKwh: life,
        nightKwh: night,
        prevYearKwh: hasPrevSeries ? prevKwh : undefined,
        isPeak: false
      });
    }

    return {
      monthlyUsage: monthlyData,
      detectedPlaceId,
      days: days || undefined,
      amountColumns,
      dataPointsCount: monthlyData.length,
      sourceType: rawSource.includes('<script') ? 'inline_script' : 'raw_js',
      summary: `Extracted ${monthlyData.length} months with measured kWh from JS amountColumns`
    };
  }

  // Otherwise: Daily Interval Telemetry (e.g. /amounts/318949/daily)
  const dailyData: EnergyDataPoint[] = [];
  const dailyAnchor = detectedDateIso || jstTodayIso();

  for (let i = 1; i < xCol.length; i++) {
    const rawX = String(xCol[i]).trim(); // e.g. "10/10"

    // A daily row must be a real `M/D` day. Month ticks (`10月`) that reach this branch used to be
    // stored as days, which corrupted the period range and inflated the reported day count.
    const tick = /^(\d{1,2})\/(\d{1,2})$/.exec(rawX);
    if (!tick) {
      console.warn('[JS Scraper] Skipping non-daily chart tick', rawX);
      continue;
    }

    const month = Number(tick[1]);
    const dayNum = Number(tick[2]);
    const timestamp = buildIsoDate(resolveTickYear(month, dailyAnchor), month, dayNum);
    if (!timestamp) {
      console.warn('[JS Scraper] Skipping impossible day tick', rawX);
      continue;
    }

    const night = Number(Number(nightCol?.[i] || 0).toFixed(2));
    const life = Number(Number(lifeCol?.[i] || 0).toFixed(2));
    const day = Number(Number(dayCol?.[i] || 0).toFixed(2));
    const genKwh = Number(Number(genericUsageCol?.[i] || 0).toFixed(2));
    const kwh = Number((night + life + day > 0 ? night + life + day : genKwh).toFixed(2));

    // Match full weekday label from days array if available (e.g. "10月10日(土)")
    const matchInDays = days?.find((d) => {
      const digitsOnly = d.replace(/[^0-9]/g, '');
      const xDigits = `${month}${dayNum}`;
      return digitsOnly.endsWith(xDigits) || d.includes(rawX);
    });

    // The portal axis carries no year, so fall back to the weekday of the resolved date.
    const weekdayChar = matchInDays?.match(/\((.)\)/)?.[1]
      ?? weekdaysJa[new Date(`${timestamp}T00:00:00`).getDay()]
      ?? '';
    const label = weekdayChar ? `${month}/${dayNum}(${weekdayChar})` : `${month}/${dayNum}`;

    const cost = Math.round(night * 18.88 + life * 32.65 + day * 26.25 + (kwh > 0 ? 34.8 : 0));

    dailyData.push({
      timestamp,
      label,
      kwh,
      cost,
      isPeak: kwh >= 2.5,
      dayKwh: day,
      lifeKwh: life,
      nightKwh: night
    });
  }

  return {
    dailyUsage: dailyData,
    detectedPlaceId,
    days: days || undefined,
    amountColumns,
    dataPointsCount: dailyData.length,
    sourceType: rawSource.includes('<script') ? 'inline_script' : 'raw_js',
    summary: `Extracted ${dailyData.length} daily interval records from JS variables (var amountColumns, var days)${detectedPlaceId ? ` for place #${detectedPlaceId}` : ''}`
  };
}

/**
 * Parses raw pasted HTML or JS code from SymEnergy Japan's portal
 * Extracts both DOM tables / headers AND inline JavaScript chart data variables.
 */
export function parseRawPortalHtml(rawHtml: string): {
  success: boolean;
  extractedAccount?: Partial<UserAccount>;
  extractedPlace?: Partial<ContractedPlace>;
  extractedPlaces?: Partial<ContractedPlace>[];
  extractedInvoices?: Partial<BillInvoice>[];
  extractedDailyUsage?: EnergyDataPoint[];
  extractedHourlyUsage?: EnergyDataPoint[];
  extractedMonthlyUsage?: EnergyDataPoint[];
  detectedPlaceId?: string;
  confirmedStatementCount?: number;
  statementPlaceId?: string;
  extractedStatements?: StatementLedgerEntry[];
  detectedFieldsCount: number;
  jsExtractedCount: number;
  jsSummary?: string;
} {
  try {
    let detected = 0;
    const extractedAccount: Partial<UserAccount> = {};
    const extractedPlace: Partial<ContractedPlace> = {};
    const extractedInvoices: Partial<BillInvoice>[] = [];
    const extractedPlaces: Partial<ContractedPlace>[] = [];
    let confirmedStatementCount: number | undefined;
    let statementPlaceId = '';
    const extractedStatements: StatementLedgerEntry[] = [];

    // 1. First, parse JavaScript telemetry variables from <script> tags or raw JS
    const jsTelemetry = extractJsTelemetryFromSource(rawHtml);
    let jsExtractedCount = 0;
    let jsSummary: string | undefined;

    if (jsTelemetry && jsTelemetry.dataPointsCount > 0) {
      detected += jsTelemetry.dataPointsCount;
      jsExtractedCount = jsTelemetry.dataPointsCount;
      jsSummary = jsTelemetry.summary;
      if (jsTelemetry.detectedPlaceId) {
        extractedAccount.currentPlaceId = jsTelemetry.detectedPlaceId;
        extractedPlace.id = jsTelemetry.detectedPlaceId;
      }
    }

    // Check for 22-digit supply point ID in raw source
    const rawSupplyMatch = rawHtml.match(/(?:供給地点特定番号|供給地点番号|特定番号|計量器番号|地点番号)[:：\s]*([0-9]{22})/i) ||
                           rawHtml.match(/\b(0[1-9]\d{20})\b/) ||
                           rawHtml.match(/["'](?:supply[_-]?point[_-]?id|mprn)["']\s*[:=]\s*["']?([0-9]{22})["']?/i);
    if (rawSupplyMatch && rawSupplyMatch[1]) {
      extractedPlace.supplyPointId = rawSupplyMatch[1];
      detected++;
    }

    // 2. Parse DOM elements if HTML tags are present
    if (rawHtml.includes('<')) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(rawHtml, 'text/html');

      // Look for account number / demand number
      const accEl = doc.querySelector('#lblAccNumber') || doc.querySelector('[id*="Account"]') || doc.querySelector('[id*="acc"]');
      if (accEl?.textContent) {
        extractedAccount.accountNumber = accEl.textContent.trim();
        detected++;
      }

      // Look for customer name
      const bTags = doc.querySelectorAll('b, strong, .c-header__right__text01');
      bTags.forEach((b) => {
        const text = b.textContent?.trim() || '';
        if (text.includes('様') || text.includes('ご契約者') || b.classList.contains('c-header__right__text01')) {
          const cleanName = text.replace(/ご契約者名?[:：]?/g, '').replace(/様/g, '').trim();
          if (!extractedAccount.accountHolder && cleanName.length > 0 && cleanName.length < 40) {
            extractedAccount.accountHolder = cleanName;
            detected++;
          }
        }
      });

      // Extract contract metadata from table rows or definition lists
      doc.querySelectorAll('tr, dl').forEach((row) => {
        const header = row.querySelector('th, dt')?.textContent?.trim() || '';
        const cell = row.querySelector('td, dd')?.textContent?.trim() || '';

        // 供給地点特定番号 (22-digit)
        if (header.includes('供給地点特定番号') || header.includes('特定番号') || header.includes('供給地点番号')) {
          const m = cell.match(/([0-9]{22})/);
          if (m && !extractedPlace.supplyPointId) {
            extractedPlace.supplyPointId = m[1];
            detected++;
          }
        }

        // ご使用場所住所
        if (header.includes('ご使用場所住所') || header.includes('使用場所住所') || header.includes('供給地点住所') || header.includes('所在地') || (header === 'ご使用場所' && cell.length > 4)) {
          if (cell && cell.length >= 4 && !extractedAccount.serviceAddress) {
            extractedAccount.serviceAddress = cell;
            extractedPlace.address = cell;
            detected++;
          }
        }

        // 契約容量 / アンペア
        if (header.includes('契約容量') || header.includes('ご契約容量') || header.includes('アンペア')) {
          const m = cell.match(/([0-9]{1,3}\s*[AkVA]+)/i);
          if (m && !extractedAccount.contractCapacity) {
            extractedAccount.contractCapacity = m[1].replace(/\s+/g, '');
            extractedPlace.contractCapacity = extractedAccount.contractCapacity;
            detected++;
          }
        }

        // 契約プラン
        if (header.includes('契約プラン') || header.includes('ご契約プラン') || header.includes('プラン名')) {
          if (cell && cell.length >= 3 && !extractedPlace.planName) {
            extractedPlace.planName = cell;
            detected++;
          }
        }
      });

      // The portal's own tables: the contracted-place roster (identity, status, plan, address,
      // payment group) and the confirmed-statement ledger. Both are authoritative, so they are read
      // for every payload instead of guessing a place from a hard-coded id.
      doc.querySelectorAll('table').forEach((table) => {
        const tableRows = Array.from(table.querySelectorAll('tr'));
        if (tableRows.length === 0) return;
        // <br> inside a header contributes no text, so `使用<br>日数` reads back as `使用日数`.
        const headers = Array.from(tableRows[0].querySelectorAll('th, td')).map(
          (cell) => (cell.textContent || '').replace(/\s+/g, '')
        );
        const bodyRows = tableRows.slice(1);

        if (isPlaceRosterHeader(headers)) {
          const rosterRows: RosterRow[] = bodyRows.map((row) => ({
            cells: Array.from(row.querySelectorAll('td, th')).map((cell) => cell.textContent || ''),
            href: row.querySelector('a[href]')?.getAttribute('href') || undefined
          }));
          const places = mapPlaceRosterRows(headers, rosterRows);
          if (places.length > 0) {
            extractedPlaces.push(...places);
            detected += places.length;
          }
          return;
        }

        // A ledger row of this statement table is a confirmed billing month, which is the only
        // honest signal that a place has issued statements yet.
        if (isStatementLedgerHeader(headers)) {
          const ledgerRows = bodyRows.map((row) =>
            Array.from(row.querySelectorAll('td')).map((cell) => cell.textContent || '')
          );
          const counted = countConfirmedStatements(ledgerRows);
          confirmedStatementCount = Math.max(confirmedStatementCount ?? 0, counted);
          const parsedStatements = mapStatementLedgerRows(headers, ledgerRows);
          if (parsedStatements.length > 0) {
            extractedStatements.length = 0;
            extractedStatements.push(...parsedStatements);
          }
          if (!statementPlaceId) statementPlaceId = contextFromUrl(readPayloadUrl(rawHtml)).placeId;
        }
      });
      // Look for billing summary table
      const table = doc.querySelector('#tblBillingSummary') || doc.querySelector('table');
      if (table) {
        const rows = table.querySelectorAll('tbody tr, tr');
        rows.forEach((row, idx) => {
          const cells = row.querySelectorAll('td');
          if (cells.length >= 4) {
            const invNum = cells[0]?.textContent?.trim();
            const amountText = cells[2]?.textContent?.trim() || '';
            const dueDateText = cells[3]?.textContent?.trim() || '';
            const statusText = cells[4]?.textContent?.trim().toLowerCase() || 'paid';

            // Extract numeric JPY value (e.g. "¥8,420" -> 8420)
            const cleanAmount = parseInt(amountText.replace(/[^0-9]/g, ''), 10) || 8420;

            if (invNum && (invNum.includes('INV') || invNum.includes('SYM'))) {
              extractedInvoices.push({
                id: `parsed-${idx}-${Date.now()}`,
                invoiceNumber: invNum,
                issueDate: '2026/10/12',
                dueDate: dueDateText || '2026/10/24',
                totalAmount: cleanAmount,
                status: statusText.includes('unpaid') || statusText.includes('未払い') || statusText.includes('due') ? 'due' : 'paid'
              });
              detected++;
            }
          }
        });
      }
    }

    // Fallback: extract address from raw text if not in DOM
    if (!extractedAccount.serviceAddress) {
      const addrMatch = rawHtml.match(/(?:ご使用場所住所|使用場所住所|供給地点住所|ご使用場所|所在地)[:：\s]*([^\r\n<]{4,80})/) ||
                        rawHtml.match(/((?:東京都|北海道|(?:京都|大阪)府|.{2,3}県)[^\r\n<]{4,60})/);
      if (addrMatch && addrMatch[1]) {
        const clean = addrMatch[1].trim();
        if (clean.length >= 4 && !clean.includes('function') && !clean.includes('var ') && !clean.includes('<')) {
          extractedAccount.serviceAddress = clean;
          extractedPlace.address = clean;
          detected++;
        }
      }
    }

    return {
      success: detected > 0,
      extractedAccount: Object.keys(extractedAccount).length > 0 ? extractedAccount : undefined,
      extractedPlace: Object.keys(extractedPlace).length > 0 ? extractedPlace : undefined,
      extractedPlaces: extractedPlaces.length > 0 ? extractedPlaces : undefined,
      extractedInvoices: extractedInvoices.length > 0 ? extractedInvoices : undefined,
      extractedDailyUsage: jsTelemetry?.dailyUsage,
      extractedHourlyUsage: jsTelemetry?.hourlyUsage,
      extractedMonthlyUsage: jsTelemetry?.monthlyUsage,
      detectedPlaceId: jsTelemetry?.detectedPlaceId || extractedAccount.currentPlaceId,
      confirmedStatementCount,
      statementPlaceId: statementPlaceId || undefined,
      extractedStatements: extractedStatements.length > 0 ? extractedStatements : undefined,
      detectedFieldsCount: detected,
      jsExtractedCount,
      jsSummary
    };
  } catch (err) {
    console.error('HTML parse error', err);
    return {
      success: false,
      detectedFieldsCount: 0,
      jsExtractedCount: 0
    };
  }
}
